import { useState } from "react";

import { type CollaboratorDetails } from "@vega-api/generated/types/collaboratorDetails";
import {
  CardVariant,
  EmptyCard,
} from "@vega-core/components/Cards/EmptyCard/EmptyCard";
import { Skeleton } from "@vega-core/components/shadcn/skeleton";
import { Switch } from "@vega-core/components/Switch/Switch";
import { CoreTable } from "@vega-core/components/Table/CoreTable/CoreTable";
import { m } from "@vega-core/paraglide/messages";

import { useCollaboratorsRolesTableConfig } from "../hooks/useCollaboratorsRolesTableConfig";

export const CollaboratorsSideDrawerRoles = ({
  selectedCollaborator,
}: {
  selectedCollaborator: CollaboratorDetails;
}) => {
  const [isGroupedByRole, setIsGroupedByRole] = useState(false);

  const {
    collaboratorHasNoPermissions,
    getUserPermissionsByRoleDataColumns,
    userPermissionsByRoleData,
    userPermissionsData,
    permissionsDataColumns,
    isRolesLoading,
  } = useCollaboratorsRolesTableConfig({
    selectedCollaborator,
  });

  if (isRolesLoading) {
    return (
      <div className="flex flex-col gap-4 pb-6">
        <div className="flex flex-row items-center justify-between">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-6 w-12" />
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <Skeleton className="h-5 w-48" />
          </div>
          <div className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <Skeleton className="h-5 w-48" />
          </div>
        </div>
      </div>
    );
  }

  if (collaboratorHasNoPermissions) {
    return (
      <EmptyCard
        description={m.common_noResultsFound()}
        variant={CardVariant.NoResults}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 pb-6">
      <div className="flex flex-row items-center justify-between">
        <div className="body-base-medium text-text/primary">
          {m.common_groupByRoles()}
        </div>

        <Switch
          checked={Boolean(isGroupedByRole)}
          id="group-by-roles-switch"
          onChange={() => setIsGroupedByRole(!isGroupedByRole)}
        />
      </div>
      {isGroupedByRole ? (
        Object.keys(userPermissionsByRoleData).map((roleId: string) => (
          <CoreTable
            columns={getUserPermissionsByRoleDataColumns(roleId)}
            data={userPermissionsByRoleData[roleId]}
            dataTestId={`collaborators-roles-table-${roleId}`}
            key={roleId}
            noSorting
          />
        ))
      ) : (
        <CoreTable
          columns={permissionsDataColumns}
          data={userPermissionsData}
          dataTestId="collaborators-roles-table"
          noSorting
        />
      )}
    </div>
  );
};
